/**
 * CSRF token access for the admin frontend.
 *
 * The backend uses a double-submit cookie pair whose two halves carry the
 * SAME value, so whatever is in the XSRF-TOKEN cookie is exactly what the
 * server expects back in the X-XSRF-TOKEN header. The cookie is deliberately
 * not httponly so this module can read it.
 */

/**
 * Get the CSRF token from the XSRF-TOKEN cookie.
 *
 * This is the authoritative source: it is the half the server compares
 * against, and the backend refreshes it on every authenticated page render.
 */
export function getCsrfToken() {
  // Anchored to a cookie boundary on purpose: an unanchored /XSRF-TOKEN=/
  // also matches inside a longer name such as OTHER-XSRF-TOKEN, and would
  // return that cookie's value instead.
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
  return match && match[1] ? decodeURIComponent(match[1]) : null
}

/**
 * Get the CSRF token from the initial page props.
 *
 * Fallback only. Inertia writes #app[data-page] on the initial full page load
 * and never updates it again, so after the first SPA navigation this returns
 * the token belonging to whichever page the session started on — and a
 * session started at /login has no token here at all. Prefer getCsrfToken().
 */
export function getCsrfTokenFromInitialPageProps() {
  try {
    const dataPageAttr = document.querySelector('#app')?.getAttribute('data-page')
    if (dataPageAttr) {
      return JSON.parse(dataPageAttr).props?.csrf_token ?? null
    }
  } catch {
    // Malformed data-page attribute — fall through to null
  }
  return null
}

/**
 * Resolve the CSRF token to submit with a state-changing request.
 *
 * Cookie first, initial page props second. Returns null when neither carries
 * one, so callers can omit the header rather than send an empty string.
 */
export function resolveCsrfToken() {
  return getCsrfToken() || getCsrfTokenFromInitialPageProps()
}
